'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import { setSession } from '@/utils/tools';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { Box, Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { forwardRef, useEffect, useRef, useState } from 'react';

interface SideNavTabsProps {
  navData: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  isDarkMode: boolean;
  activeTabIndex?: number;
  onTabMouseEnter?: (index: number) => void;
  onTabMouseLeave?: () => void;
  onSubnodeClick?: () => void;
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

// 自定义Tab组件，使用MagicJump包裹a标签
const CustomTab = forwardRef<HTMLElement, any>((props, ref) => {
  const { item, index, children, ...other } = props;
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();

  const jumpTarget = {
    jump_type: item?.content?.jump_type || 3,
    Jump_target: item?.content?.Jump_target || '#',
    Jump_title: item?.content?.nav_name || '',
    product_group: {
      id: item?.content?.product_group?.id,
      path: item?.content?.product_group?.path,
    },
    sku: {
      shop_sku: item?.content?.sku?.shop_sku,
    },
    crowd_recommend: {
      id: item?.content?.crowd_recommend?.id,
    },
  };

  return (
    <MagicJump
      item={jumpTarget}
      onBeforeJump={() => {
        setCampaignEntrancePageScene('顶部导航', item?.content?.Jump_target);
        if (
          +item?.content?.jump_type === 1 ||
          `${item?.content?.Jump_target}`?.includes('goods-list')
        ) {
          setSession('zl_goodsListEnterPageScene', '全局导航');
        }
        trackEvent(
          {
            eventName: 'TopNavTab_Click',
            properties: {
              page_name: cnName,
              url_path: window.location.href,
              tab_name: item?.content?.nav_name,
              tab_index: index,
            },
          },
          [ProviderType.SENSORS],
        );
      }}
      ref={ref}
      {...other}
    >
      {children}
    </MagicJump>
  );
});

CustomTab.displayName = 'CustomTab';

const SideNavTabs = ({
  navData,
  isDarkMode,
  activeTabIndex = -1,
  onTabMouseEnter,
  onTabMouseLeave,
  onSubnodeClick,
  containerRef: externalRef,
}: SideNavTabsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = externalRef || internalRef;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!containerRef.current || !onSubnodeClick) return;
    const handleSubnodeClick = (e: MouseEvent) => {
      let currentTarget = e.target as HTMLElement;
      if (!currentTarget) return;
      do {
        if (currentTarget === containerRef.current) return;
        if (currentTarget.getAttribute('href')) {
          onSubnodeClick();
          return;
        }
        currentTarget = currentTarget.parentElement;
      } while (currentTarget);
    };
    containerRef.current.addEventListener('click', handleSubnodeClick, true);
    return () => {
      containerRef.current?.removeEventListener('click', handleSubnodeClick, true);
    };
  }, [onSubnodeClick]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isHydrated || !onTabMouseEnter) return;
    const target = e.target as HTMLElement;
    const tabElement = target.closest('[role="tab"]');
    if (tabElement) {
      const tabs = Array.from(tabElement.parentElement?.children || []);
      const tabIndex = tabs.indexOf(tabElement);
      if (tabIndex >= 0 && tabIndex < navData.length) {
        onTabMouseEnter(tabIndex);
      }
    }
  };

  const handleMouseLeave = () => {
    if (onTabMouseLeave) onTabMouseLeave();
  };

  // 静态基础样式
  const baseSx = {
    flex: 1,
    alignSelf: 'stretch',
    // 覆盖 Header 的 1px 底边框，让选中线与灰色分割线重合。
    mb: '-1px',
    minWidth: '0',
    ml: '56px',
    transition: 'all 0.3s',
    '& .MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
      fontFamily: font.br,
      boxSizing: 'border-box',
      height: '100%',
      fontSize: '16px',
      color: isDarkMode ? '#ccc !important' : colors.primaryBlack,
    },
    '& .MuiTabs-indicator': {
      display: 'none!important',
    },
    '& .MuiTabs-root, & .MuiTabs-scroller, & .MuiTabs-list': {
      height: '100%',
    },
    '& .MuiTabs-list': {
      gap: 'min(2.34375vw, 45px);',
    },
    '@media (max-width: 1536px)': {
      '&': { ml: '45px' },
    },
    '@media (max-width: 1366px)': {
      '&': { ml: '32px' },
      '& .MuiTabs-list': { gap: '24px' },
    },
    '@media (max-width: 1280px)': {
      '&': { display: 'none' },
    },
  };

  // 交互增强样式（仅水合后生效）
  const interactiveSx = isHydrated
    ? {
        '& .MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary:hover, & .MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary.Mui-selected':
          {
            fontFamily: font.bb,
            color: isDarkMode ? '#ccc !important' : colors.primaryBlackHover,
            borderBottom: `2px solid ${isDarkMode ? '#ccc !important' : colors.primaryBlackHover}`,
            borderTop: '2px solid transparent',
            letterSpacing: '-0.4px',
          },
        '& .MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary.Mui-selected': {
          fontWeight: 'normal',
        },
        '& .MuiTabScrollButton-root:first-child': {
          order: 1,
          width: '12px',
          padding: '10px',
          ml: '6px',
          color: isDarkMode ? '#fff' : '#646464',
        },
        '& .MuiTabScrollButton-root:last-child': {
          order: 2,
          width: '12px',
          padding: '6px',
          mr: '12px',
          color: isDarkMode ? '#fff' : '#646464',
        },
      }
    : {};

  return (
    <Box
      ref={containerRef}
      sx={{ ...baseSx, ...interactiveSx }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <MuiTabs
        value={activeTabIndex >= 0 ? activeTabIndex : false}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{ '& .MuiTab-root': { paddingLeft: '0', paddingRight: '0' } }}
      >
        {navData.map((item, index) => (
          <MuiTab
            key={item.id.toString()}
            label={item.content?.nav_name}
            component={CustomTab}
            item={item}
            index={index}
            sx={{ overflow: 'unset' }}
          />
        ))}
      </MuiTabs>
    </Box>
  );
};

export default SideNavTabs;
